import { SoftwareTypeSchema } from '@terra/shared-contracts-external-network';
import type { FilterConfig } from '@terra/ui-design-system';
import { humanizeSnakeCase } from '../../internal-network/scenarios/string.utils';
import type { ExternalNetworkEntityKindFilter } from './surfaceAsset.constants';

export type AssetFilterOptionLists = {
  products: string[];
  cveIds: string[];
  regions: string[];
  organizations: string[];
};

const SCOPE_VALUES = ['IN_SCOPE', 'OUT_OF_SCOPE'] as const;

export const EMPTY_ASSET_FILTER_OPTIONS: AssetFilterOptionLists = {
  products: [],
  cveIds: [],
  regions: [],
  organizations: [],
};

function toValueItems(values: string[]) {
  return values.map((value) => ({ value, label: value }));
}

function scopeFilter({ scopeLabel }: { scopeLabel: string }): FilterConfig {
  return {
    id: 'scope',
    items: SCOPE_VALUES.map((value) => ({
      value,
      label: humanizeSnakeCase(value),
    })),
    placeholder: scopeLabel,
  };
}

function technologyFilter({
  technologyLabel,
}: {
  technologyLabel: string;
}): FilterConfig {
  return {
    id: 'softwareType',
    items: SoftwareTypeSchema.options.map((value) => ({
      value,
      label: humanizeSnakeCase(value),
    })),
    placeholder: technologyLabel,
  };
}

function softwareRelationFilters({
  softwareLabel,
  technologyLabel,
  cveLabel,
  products,
  cveIds,
}: {
  softwareLabel: string;
  technologyLabel: string;
  cveLabel: string;
  products: string[];
  cveIds: string[];
}): FilterConfig[] {
  return [
    {
      id: 'product',
      items: toValueItems(products),
      placeholder: softwareLabel,
    },
    technologyFilter({ technologyLabel }),
    {
      id: 'cveId',
      items: toValueItems(cveIds),
      placeholder: cveLabel,
    },
  ];
}

export function buildAssetFilterConfigs({
  selectedKind,
  softwareLabel,
  technologyLabel,
  cveLabel,
  regionLabel,
  organizationLabel,
  scopeLabel,
  options,
}: {
  selectedKind: ExternalNetworkEntityKindFilter;
  softwareLabel: string;
  technologyLabel: string;
  cveLabel: string;
  regionLabel: string;
  organizationLabel: string;
  scopeLabel: string;
  options: AssetFilterOptionLists;
}): FilterConfig[] {
  if (!selectedKind) {
    return [
      scopeFilter({ scopeLabel }),
      ...softwareRelationFilters({
        softwareLabel,
        technologyLabel,
        cveLabel,
        products: options.products,
        cveIds: options.cveIds,
      }),
      {
        id: 'region',
        items: toValueItems(options.regions),
        placeholder: regionLabel,
      },
      {
        id: 'organization',
        items: toValueItems(options.organizations),
        placeholder: organizationLabel,
      },
    ];
  }

  switch (selectedKind) {
    case 'DOMAIN':
    case 'HOST':
    case 'NETWORK_SERVICE':
      return [
        scopeFilter({ scopeLabel }),
        ...softwareRelationFilters({
          softwareLabel,
          technologyLabel,
          cveLabel,
          products: options.products,
          cveIds: options.cveIds,
        }),
      ];
    case 'SOFTWARE':
      return softwareRelationFilters({
        softwareLabel,
        technologyLabel,
        cveLabel,
        products: options.products,
        cveIds: options.cveIds,
      });
    case 'GATEWAY':
      return [
        scopeFilter({ scopeLabel }),
        technologyFilter({ technologyLabel }),
      ];
    case 'STORAGE_SERVICE':
      return [
        scopeFilter({ scopeLabel }),
        {
          id: 'region',
          items: toValueItems(options.regions),
          placeholder: regionLabel,
        },
      ];
    case 'NETBLOCK':
      return [
        scopeFilter({ scopeLabel }),
        {
          id: 'region',
          items: toValueItems(options.regions),
          placeholder: regionLabel,
        },
        {
          id: 'organization',
          items: toValueItems(options.organizations),
          placeholder: organizationLabel,
        },
      ];
    default:
      return [];
  }
}
